import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  MapPin,
  BadgePercent,
  Award,
  Headphones,
  GraduationCap,
  CheckCircle2,
  Star,
  Sparkles,
} from "lucide-react";
import { UNIVERSITIES, BENEFITS, LOGOS, ALL_COURSES_FLAT } from "../data/universities";
import FAQ from "../components/FAQ";
import NewsletterCTA from "../components/NewsletterCTA";

const iconMap = {
  users: Users,
  "map-pin": MapPin,
  "badge-percent": BadgePercent,
  award: Award,
  headphones: Headphones,
  "graduation-cap": GraduationCap,
};

export default function Home() {
  const totalLaurea = ALL_COURSES_FLAT.filter((c) => c.level !== "Master").length;
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative pt-28 pb-24 lg:pt-36 lg:pb-32 overflow-hidden bg-brand-cream">
        <img
          src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Studenti in biblioteca universitaria"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-cream via-brand-cream/95 to-white/80" />
        <div className="absolute inset-0 dot-pattern opacity-[0.05]" />
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-emerald/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-brand-gold/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-brand-navy">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-brand-line text-[12px] font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5 text-brand-emerald" /> Polo ECP Profili · Sassari
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              La tua laurea online.<br />
              <span className="text-brand-emerald">Tre atenei</span>, una sola sede.
            </h1>
            <p className="mt-6 text-lg text-brand-muted max-w-xl leading-relaxed">
              Scegli tra <span className="font-semibold text-brand-navy">Pegaso</span>,{" "}
              <span className="font-semibold text-brand-navy">Mercatorum</span> e{" "}
              <span className="font-semibold text-brand-navy">San Raffaele</span>. Tutti i corsi di laurea triennale,
              magistrale e master con tutor dedicato in Via Forlanini 18.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contatti" className="btn-primary" data-testid="hero-iscriviti-cta">
                Iscriviti Ora <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/orientamento" className="btn-secondary" data-testid="hero-info-cta">
                Richiedi Informazioni
              </Link>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-brand-muted text-sm" data-testid="hero-trust">
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-brand-emerald" /> Dal 1960 a Sassari</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-brand-emerald" /> +1.000 studenti laureati</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-brand-emerald" /> Tutor in presenza</span>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-xl">
              <StatLight n="3" label="Atenei MIUR" />
              <StatLight n={`${totalLaurea}+`} label="Corsi di laurea" />
              <StatLight n="95" label="Sedi d'esame" />
              <StatLight n="-50%" label="Sulla retta" />
            </div>
          </div>

          {/* Offer card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl bg-white p-7 lg:p-8 shadow-2xl" data-testid="hero-offer-card">
              <div className="absolute -top-3 left-7 px-3 py-1.5 rounded-full bg-brand-gold text-brand-navy text-[11px] font-bold uppercase tracking-wider shadow-md">
                Offerta Attiva
              </div>
              <div className="eyebrow text-brand-muted">Convenzione Polo Profili</div>
              <h3 className="font-heading font-extrabold text-3xl text-brand-navy mt-2">
                Fino a -50% sulla retta
              </h3>
              <div className="mt-1 text-brand-ink text-lg font-semibold">
                Rate da <span className="text-brand-emerald">125 € / mese</span>
              </div>
              <ul className="mt-5 space-y-3 text-sm text-brand-ink">
                {[
                  "Iscrizione assistita e gratuita",
                  "Tutor dedicato fino alla laurea",
                  "Certificazioni EIPASS incluse",
                  "Esami in 95 sedi in Italia",
                ].map((t) => (
                  <li key={t} className="flex gap-2.5">
                    <CheckCircle2 className="w-4.5 h-4.5 text-brand-emerald shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
              <Link to="/contatti" className="btn-primary w-full justify-center mt-6" data-testid="offer-card-cta">
                Richiedi Preventivo Gratuito
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — partner logos */}
      <section className="bg-white border-b border-brand-line py-10" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-6 eyebrow text-brand-muted">Riconosciuti MIUR · Polo Profili Sassari</div>
          <div className="grid grid-cols-2 md:grid-cols-5 items-center gap-8 lg:gap-12">
            <div className="flex justify-center" data-testid="partner-logo-europa">
              <img src={LOGOS.europa} alt="Istituto Europa Sassari" className="h-16 lg:h-20 object-contain" />
            </div>
            <div className="flex justify-center" data-testid="partner-logo-multiversity">
              <div className="inline-flex items-center justify-center bg-brand-navy rounded-xl px-5 py-2.5 shadow-sm">
                <img src={LOGOS.multiversity} alt="Multiversity" className="h-7 lg:h-8 object-contain" />
              </div>
            </div>
            <div className="flex justify-center" data-testid="partner-logo-pegaso">
              <img src={LOGOS.pegaso} alt="Università Telematica Pegaso" className="h-12 lg:h-14 object-contain grayscale hover:grayscale-0 transition-all duration-500" />
            </div>
            <div className="flex justify-center" data-testid="partner-logo-mercatorum">
              <img src={LOGOS.mercatorum} alt="Università Mercatorum" className="h-12 lg:h-14 object-contain grayscale hover:grayscale-0 transition-all duration-500" />
            </div>
            <div className="flex justify-center" data-testid="partner-logo-sanraffaele">
              <img src={LOGOS.sanraffaele} alt="Università San Raffaele Roma" className="h-12 lg:h-14 object-contain grayscale hover:grayscale-0 transition-all duration-500" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY POLO */}
      <section className="py-20 lg:py-24 bg-white relative" data-testid="why-polo-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-5">
              <div className="eyebrow text-brand-emerald mb-3">Perché scegliere il Polo</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[1.05]">
                Non affrontarlo da solo.<br />
                <span className="text-brand-emerald">Fai la scelta giusta.</span>
              </h2>
              <p className="mt-6 text-brand-muted text-lg leading-relaxed">
                Iscriversi a un'università telematica è una decisione importante: scegliere il corso adatto, gestire la burocrazia e valutare le migliori convenzioni richiede tempo ed esperienza. Con il Polo ECP Profili hai al tuo fianco un team dedicato che ti accompagna in ogni fase e ti offre un punto di riferimento reale.
              </p>
              <Link to="/orientamento" className="btn-primary mt-7 inline-flex" data-testid="why-polo-cta">
                Come funziona <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: "€", title: "Retta scontata fino al -50%", desc: "Convenzioni esclusive del Polo. Iscrivendoti da solo paghi il prezzo pieno.", positive: true },
                  { icon: "★", title: "Tutor a tua disposizione 24 ore su 24", desc: "Scrivi al tutor quando hai necessità. Sarà sempre disponibile per aiutarti.", positive: true },
                  { icon: "✎", title: "Iscrizione assistita passo passo", desc: "Compiliamo insieme i documenti. Da soli è un labirinto burocratico.", positive: true },
                  { icon: "✓", title: "Piano di studi personalizzato", desc: "Valutiamo esami convalidabili e ottimizziamo il tuo percorso.", positive: true },
                ].map((b, i) => (
                  <div key={i} className="relative rounded-2xl bg-brand-surface border border-brand-line p-6 lift-card" data-testid={`why-polo-benefit-${i}`}>
                    <div className="w-11 h-11 rounded-xl bg-brand-emerald text-white flex items-center justify-center font-heading font-extrabold text-xl mb-4">
                      {b.icon}
                    </div>
                    <h3 className="font-heading font-bold text-lg text-brand-navy leading-tight">{b.title}</h3>
                    <p className="text-sm text-brand-muted mt-2 leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl bg-brand-navy text-white p-5 flex items-center gap-4" data-testid="why-polo-callout">
                <div className="w-11 h-11 rounded-xl bg-brand-gold text-brand-navy flex items-center justify-center font-heading font-extrabold text-xl shrink-0">!</div>
                <div className="text-sm">
                  <span className="font-bold text-brand-gold">100% gratuito.</span> L'orientamento e l'assistenza all'iscrizione presso il Polo non ti costano nulla in più: paghi la retta scontata all'ateneo.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UNIVERSITIES */}
      <section className="py-20 lg:py-28 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow text-brand-emerald mb-3">I 3 atenei del polo</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-navy">
              Pegaso, Mercatorum, San Raffaele.
            </h2>
            <p className="mt-4 text-brand-muted text-lg leading-relaxed">
              Tre università telematiche riconosciute dal MIUR. Un solo Polo ECP a Sassari per gestire iscrizione, esami e tutoraggio.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {UNIVERSITIES.map((u) => (
              <Link
                key={u.id}
                to={`/universita#${u.id}`}
                className="lift-card group rounded-3xl overflow-hidden bg-white border border-brand-line"
                data-testid={`uni-card-${u.id}`}
              >
                <div className="relative h-44 overflow-hidden flex items-center justify-center bg-white p-8 border-b border-brand-line">
                  <div className="absolute inset-0 opacity-[0.06]" style={{ background: `radial-gradient(circle at 30% 50%, ${u.color}, transparent 60%)` }} />
                  <img src={u.logo} alt={u.name} className="relative max-h-24 max-w-[80%] object-contain group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-4 right-4 text-[9px] font-bold tracking-[0.2em] uppercase px-2 py-1 rounded-full" style={{ background: `${u.color}15`, color: u.color }}>
                    {u.badge}
                  </span>
                </div>
                <div className="p-6">
                  <div className="font-heading font-extrabold text-xl text-brand-navy">{u.short}</div>
                  <div className="text-sm font-semibold mt-0.5" style={{ color: u.color }}>{u.tagline}</div>
                  <p className="text-sm text-brand-muted leading-relaxed mt-3">{u.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-xs">
                      <span className="font-bold text-brand-navy">{u.students}</span>
                      <span className="text-brand-muted"> studenti</span>
                    </div>
                    <span className="text-sm font-bold text-brand-emerald inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Scopri corsi <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="eyebrow text-brand-emerald mb-3">Perché ECP Profili</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[1.05]">
                Più di un centro esami.<br />Un <span className="text-brand-emerald">team</span> al tuo fianco.
              </h2>
            </div>
            <p className="lg:col-span-5 text-brand-muted text-lg leading-relaxed">
              Dalla scelta del corso alla discussione della tesi: ti accompagniamo in ogni passaggio, con persone vere e supporto in presenza.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {BENEFITS.map((b) => {
              const Icon = iconMap[b.icon] || Users;
              return (
                <div key={b.title} className="lift-card rounded-2xl bg-brand-surface border border-brand-line p-7" data-testid={`benefit-${b.icon}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-navy mb-2">{b.title}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STORY / TRUST */}
      <section className="py-20 lg:py-28 bg-white relative overflow-hidden border-t border-brand-line">
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow text-brand-emerald mb-3">Dal 1960 a Sassari</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-brand-navy">
              Una storia di formazione,<br />una sede moderna.
            </h2>
            <p className="mt-6 text-brand-muted text-lg leading-relaxed">
              L'Istituto Europa nasce negli anni '60 come scuola di lingue, evolve in scuola paritaria nel 2003 ed è oggi anche Polo ECP riconosciuto per le tre principali università telematiche italiane.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <Stat n="60+" label="Anni di attività" muted />
              <Stat n="2003" label="Scuola paritaria" muted />
              <Stat n="3" label="Atenei convenzionati" muted />
            </div>
            <div className="mt-8 flex gap-3">
              <Link to="/chi-siamo" className="btn-secondary" data-testid="story-chi-siamo-cta">Scopri la storia</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1606761568499-6d2451b23c66?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="" className="rounded-2xl h-56 w-full object-cover" />
              <img src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="" className="rounded-2xl h-72 w-full object-cover translate-y-6" />
              <img src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="" className="rounded-2xl h-72 w-full object-cover -translate-y-6" />
              <img src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="" className="rounded-2xl h-56 w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow text-brand-emerald mb-3">Studenti del polo</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-brand-navy">Cosa dicono di noi</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Maria T.", course: "Pegaso · Psicologia", text: "Tutor sempre presente, ho conciliato lavoro e laurea senza stress. Sedi d'esame ovunque." },
              { name: "Luca F.", course: "Mercatorum · MBA", text: "Convenzione applicata subito, rate sostenibili. Iscrizione assistita in pochi giorni." },
              { name: "Giulia P.", course: "San Raffaele · Nutrizione", text: "Materiale ottimo e supporto reale, non solo online. Polo Profili davvero il punto di riferimento a Sassari." },
            ].map((t) => (
              <div key={t.name} className="rounded-2xl bg-brand-surface p-7 border border-brand-line" data-testid={`testimonial-${t.name.split(" ")[0]}`}>
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-brand-emerald text-brand-emerald" />)}
                </div>
                <p className="text-brand-ink leading-relaxed">"{t.text}"</p>
                <div className="mt-5 pt-5 border-t border-brand-line">
                  <div className="font-bold text-brand-navy">{t.name}</div>
                  <div className="text-sm text-brand-muted">{t.course}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-24 bg-brand-cream">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl bg-white border border-brand-line p-10 lg:p-16 text-center relative overflow-hidden shadow-xl">
            <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-emerald/8 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-brand-gold/8 blur-3xl" />
            <div className="relative">
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-navy">
                Pronto a iniziare? <br className="hidden sm:block" />
                <span className="text-brand-emerald">Orientamento gratuito in 48h.</span>
              </h2>
              <p className="mt-5 text-brand-muted text-lg max-w-xl mx-auto">
                Compila il form, un tutor del Polo ti contatterà per la migliore scelta tra Pegaso, Mercatorum e San Raffaele.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/contatti" className="btn-primary" data-testid="final-cta-iscriviti">
                  Iscriviti Ora <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/orientamento" className="btn-secondary" data-testid="final-cta-orientamento">
                  Richiedi Orientamento
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQ />
      <NewsletterCTA />
    </div>
  );
}

function Stat({ n, label, muted = false }) {
  return (
    <div>
      <div className="font-heading font-extrabold text-3xl text-brand-emerald">{n}</div>
      <div className={`text-xs uppercase tracking-wider font-semibold mt-1 ${muted ? "text-brand-muted" : "text-brand-muted"}`}>{label}</div>
    </div>
  );
}

function StatLight({ n, label }) {
  return (
    <div>
      <div className="font-heading font-extrabold text-3xl text-brand-emerald">{n}</div>
      <div className="text-xs uppercase tracking-wider font-semibold mt-1 text-brand-muted">{label}</div>
    </div>
  );
}
